import { useMemo } from "react";
import { motion } from "framer-motion";
import { Sparkles, Palette, Layers, Tag } from "lucide-react";
import { getActiveProjectGraph } from "../../data/sampleGraph";
import type { GraphNode } from "../../types/ontology";
import { cls } from "../../lib/cls";

/* MoodBoard — project-grounded inspiration.
 *
 * Drops the old "Kaggle #N · dataset" engineer leakage and re-renders
 * around three layers pulled from the current project's typed graph:
 *
 *   1. Intent + Style nodes      → headline mood
 *   2. Rooms (with materials)    → per-room palette swatches
 *   3. SKUs grouped by material  → material story
 *
 * Apple-grade: cream/ink palette, generous whitespace, no chrome,
 * no model names, no dataset references. Every tile cites a real
 * entity on this project. */

interface PaletteSwatch {
  hex: string;
  label: string;
}

/* Cream/ink-aligned palette — these are *our* design tokens, not
 * lifted from anyone else. They paint a swatch row per room based
 * on room kind and materials present. */
const MATERIAL_SWATCH: Record<string, PaletteSwatch> = {
  "Engineered oak":     { hex: "#C6A981", label: "Oak warm" },
  "Brushed brass":      { hex: "#B4924E", label: "Brushed brass" },
  "Calacatta quartz":   { hex: "#F2EFE9", label: "Calacatta" },
  "Mughal-pillar":      { hex: "#7E4B33", label: "Mughal teak" },
  "Scandinavian-Indo":  { hex: "#E6E2DC", label: "Soft linen" },
};

const ROOM_ACCENT: Record<string, string> = {
  Living: "#C95C1A",
  "Master Bedroom": "#3D6B9D",
  "Kitchen (modular)": "#3F7A55",
  "Kids Bedroom": "#A0688B",
};

function paletteFor(room: GraphNode, materials: GraphNode[]): PaletteSwatch[] {
  const out: PaletteSwatch[] = [];
  const accent = ROOM_ACCENT[room.label] ?? "#94908a";
  out.push({ hex: accent, label: `${room.label} accent` });
  for (const m of materials.slice(0, 3)) {
    const sw = MATERIAL_SWATCH[m.label] ?? { hex: "#D9D2C5", label: m.label };
    out.push(sw);
  }
  // Always include the cream/ink twins so the swatch reads as a
  // complete palette.
  out.push({ hex: "#F8F4EC", label: "Cream base" });
  out.push({ hex: "#1A1A1A", label: "Ink contrast" });
  return out;
}

export function MoodBoard(): JSX.Element {
  const { project, intent, styles, rooms, materials } = useMemo(() => {
    const nodes = getActiveProjectGraph().nodes;
    return {
      project:   nodes.find((n) => n.kind === "Project"),
      intent:    nodes.find((n) => n.kind === "Intent"),
      styles:    nodes.filter((n) => n.kind === "Style"),
      rooms:     nodes.filter((n) => n.kind === "Room"),
      materials: nodes.filter((n) => n.kind === "Material"),
    };
  }, []);

  return (
    <div className="space-y-4">
      <Header project={project} intent={intent} />
      <StyleRow styles={styles} />
      <RoomPaletteGrid rooms={rooms} materials={materials} />
      <MaterialStory materials={materials} />
    </div>
  );
}

function Header({ project, intent }: { project: GraphNode | undefined; intent: GraphNode | undefined }): JSX.Element {
  return (
    <section className="panel-hero p-5 sm:p-6 relative overflow-hidden">
      <div className="absolute inset-0 dot-grid opacity-30 pointer-events-none" />
      <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full bg-foyr/12 blur-3xl pointer-events-none" />
      <div className="relative flex items-start gap-3">
        <span className="w-10 h-10 rounded-xl bg-ink-950 text-cream grid place-items-center shrink-0">
          <Palette className="w-4 h-4" />
        </span>
        <div className="min-w-0">
          <div className="eyebrow">Inspiration · {project?.label ?? "this project"}</div>
          <div className="display-2 text-ink-950 mt-0.5 leading-[1.04]">
            {intent ? `“${intent.label}”` : "Mood not set yet."}
          </div>
          <div className="text-[13px] text-ink-700 mt-1.5 max-w-2xl leading-snug">
            Every swatch + material below is tied to a typed node on this project.
            Designer's brief → ontology → moodboard, in one place.
          </div>
        </div>
      </div>
    </section>
  );
}

function StyleRow({ styles }: { styles: GraphNode[] }): JSX.Element {
  if (styles.length === 0) return <></>;
  return (
    <section className="panel p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-3">
        <Tag className="w-3.5 h-3.5 text-ink-500" />
        <span className="eyebrow">Style anchors</span>
        <span className="text-[11px] text-ink-500">· {styles.length} typed style nodes on this project</span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {styles.map((s) => (
          <span
            key={s.id}
            className="pill text-[12px] bg-cream-dark border border-border text-ink-950"
            title={typeof s.attrs["dataset"] === "string" ? "Studio style library" : undefined}
          >
            <Sparkles className="w-3 h-3 text-foyr" /> {s.label}
          </span>
        ))}
      </div>
    </section>
  );
}

function RoomPaletteGrid({ rooms, materials }: { rooms: GraphNode[]; materials: GraphNode[] }): JSX.Element {
  return (
    <section>
      <div className="flex items-center gap-2 mb-2">
        <Layers className="w-3.5 h-3.5 text-ink-500" />
        <span className="eyebrow">Per-room palette</span>
        <span className="text-[11px] text-ink-500">· derived from typed rooms + materials</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {rooms.map((r, i) => {
          const swatches = paletteFor(r, materials);
          return (
            <motion.article
              key={r.id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.22, delay: i * 0.04 }}
              className="panel p-4 hover:shadow-deep transition-all"
            >
              <div className="flex items-baseline justify-between gap-3">
                <div className="text-[14px] font-semibold text-ink-950 leading-tight">{r.label}</div>
                <div className="text-[10.5px] text-ink-500 tabular font-mono">
                  {typeof r.attrs["area"] === "number" ? `${r.attrs["area"]} sqft` : "area —"}
                  {typeof r.attrs["ceilingHt"] === "number" ? ` · ${r.attrs["ceilingHt"]} ft H` : ""}
                </div>
              </div>
              <div className="mt-3 flex items-stretch gap-1">
                {swatches.map((s, idx) => (
                  <div
                    key={`${r.id}-${idx}`}
                    className="flex-1 rounded-lg overflow-hidden border border-border/50 aspect-[3/4] grid place-items-end p-1"
                    style={{ background: s.hex }}
                    title={s.label}
                  >
                    <span className={cls(
                      "text-[8.5px] font-mono uppercase tracking-wider px-1 py-0.5 rounded-sm",
                      isDark(s.hex) ? "bg-black/30 text-cream" : "bg-cream/70 text-ink-950",
                    )}>
                      {s.label}
                    </span>
                  </div>
                ))}
              </div>
            </motion.article>
          );
        })}
      </div>
    </section>
  );
}

function MaterialStory({ materials }: { materials: GraphNode[] }): JSX.Element {
  if (materials.length === 0) return <></>;
  return (
    <section className="panel p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-3.5 h-3.5 text-ink-500" />
        <span className="eyebrow">Materials</span>
        <span className="text-[11px] text-ink-500">· {materials.length} typed material nodes</span>
      </div>
      <ul className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
        {materials.map((m) => {
          const sw = MATERIAL_SWATCH[m.label] ?? { hex: "#D9D2C5", label: m.label };
          return (
            <li key={m.id} className="rounded-xl border border-border p-3 flex items-center gap-3 bg-cream">
              <span className="w-9 h-9 rounded-lg border border-border/60 shrink-0" style={{ background: sw.hex }} />
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-ink-950 leading-tight truncate">{m.label}</div>
                <div className="text-[10.5px] text-ink-500">Stored as a typed Material node</div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function isDark(hex: string): boolean {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum < 0.55;
}
